/**
 * Client-safe version descriptor.
 *
 * Values are baked into the client bundle via `NEXT_PUBLIC_*` env vars at
 * build time. This module must NEVER import `fs` or the server-side
 * `lib/version.ts` - it ships to the browser.
 */

export interface ClientVersionInfo {
  service: 'frontend'
  version: string
  gitSha: string
  gitShaFull: string
  buildTime: string
  releaseTag: string
  environment: string
}

const DEFAULT_SHA = 'dev';

const normalizeSha = (value: string | undefined): { short: string; full: string } => {
  if (!value || value === DEFAULT_SHA) {
    return { short: DEFAULT_SHA, full: DEFAULT_SHA };
  }
  return { short: value.slice(0, 7), full: value };
};

const toDateSlug = (iso: string): string => {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

const computeClientVersionInfo = (): ClientVersionInfo => {
  const rawSha = process.env.NEXT_PUBLIC_GIT_SHA || DEFAULT_SHA;
  const buildTime = process.env.NEXT_PUBLIC_BUILD_TIME || new Date().toISOString();
  const environment =
    process.env.NEXT_PUBLIC_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const version = process.env.NEXT_PUBLIC_APP_VERSION || '0.0.0';

  const { short, full } = normalizeSha(rawSha);

  return Object.freeze({
    service: 'frontend',
    version,
    gitSha: short,
    gitShaFull: full,
    buildTime,
    releaseTag: `frontend-${toDateSlug(buildTime)}-${short}`,
    environment,
  });
};

export const CLIENT_VERSION_INFO: ClientVersionInfo = computeClientVersionInfo();

declare global {
  interface Window {
    __PUREBOX_VERSION__?: ClientVersionInfo
  }
}
